import type { FC } from 'react';
import { forwardRef, useCallback, useEffect, useId } from 'react';
import FreeTextInput from '../FreeTextInput/FreeTextInput.component';
import useGoogleAddressAutocomplete from 'hooks/shared/useGoogleAddressAutocomplete';
import type AddressAutocompleteInputProps from './AddressAutocompleteInput.types';
import classNames from 'classnames';

const getOptionId = (listboxId: string, index: number) =>
  `${listboxId}-option-${index}`;

/**
 * Drop-in replacement for FreeTextInput with Google Places Autocomplete.
 * Uses the Places API (New) REST endpoint — no Maps JavaScript SDK required.
 * Falls back gracefully to a plain input when the API key is absent.
 */
const AddressAutocompleteInput: FC<AddressAutocompleteInputProps> = forwardRef(
  (
    {
      onAddressSelect,
      countryRestrictions,
      includedPrimaryTypes,
      locationHint,
      onChange,
      onBlur,
      ...props
    },
    forwardedRef,
  ) => {
    const listboxId = useId();

    const {
      suggestions,
      isOpen,
      activeIndex,
      handleInputChange,
      handleSuggestionSelect,
      handleKeyDown,
      closeDropdown,
    } = useGoogleAddressAutocomplete({
      onPlaceSelect: onAddressSelect,
      countryRestrictions,
      includedPrimaryTypes,
      locationHint,
    });

    const expanded = isOpen && suggestions.length > 0;

    useEffect(() => {
      if (activeIndex < 0) return;
      document
        .getElementById(getOptionId(listboxId, activeIndex))
        ?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, listboxId]);

    // Stable callback ref — recreating it on every render would cause React to
    // detach/reattach the DOM node, losing focus after each keystroke.
    const setRef = useCallback(
      (node: HTMLInputElement | null) => {
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (
            forwardedRef as React.MutableRefObject<HTMLInputElement | null>
          ).current = node;
        }
      },
      [forwardedRef],
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (onChange as any)?.(e);
        handleInputChange(e.target.value);
      },
      [onChange, handleInputChange],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        // Delay so a mousedown on a suggestion fires before the dropdown closes.
        setTimeout(closeDropdown, 150);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (onBlur as any)?.(e);
      },
      [onBlur, closeDropdown],
    );

    return (
      <div className="relative">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <FreeTextInput
          {...props}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? listboxId : undefined}
          aria-activedescendant={
            expanded && activeIndex >= 0
              ? getOptionId(listboxId, activeIndex)
              : undefined
          }
          onChange={handleChange as any}
          onBlur={handleBlur as any}
          onKeyDown={handleKeyDown as any}
          ref={setRef as any}
        />

        {expanded && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Address suggestions"
            onMouseDown={(e) => e.preventDefault()}
            className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <li
                id={getOptionId(listboxId, index)}
                key={suggestion.placeId}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  void handleSuggestionSelect(suggestion);
                }}
                className={classNames(
                  'flex flex-col px-3 py-2 cursor-pointer text-left',
                  {
                    'bg-gray-100': index === activeIndex,
                    'hover:bg-gray-50': index !== activeIndex,
                  },
                )}
              >
                <span className="text-sm font-medium text-gray-900">
                  {suggestion.mainText}
                </span>
                {suggestion.secondaryText && (
                  <span className="text-xs text-gray-500">
                    {suggestion.secondaryText}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Visually hidden live region — announces suggestion count to screen readers */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {expanded
            ? `${suggestions.length} suggestion${
                suggestions.length === 1 ? '' : 's'
              } available`
            : ''}
        </div>
      </div>
    );
  },
);

AddressAutocompleteInput.displayName = 'AddressAutocompleteInput';

export default AddressAutocompleteInput;
